/**
 * Floating AI Jobs tray — bottom-right of the viewport, collapsible.
 * Shows a card per in-flight or recently-completed job from the AiJobs
 * context. Adding a new kind is zero changes here — driven by KIND_CONFIG.
 */
import React, { useMemo, useState } from "react";
import { useAiJobs, KIND_CONFIG } from "@/lib/ai-jobs";
import { Button } from "@/components/ui/button";
import {
    ScrollText, Brain, Film, ChevronUp, ChevronDown, X, CheckCircle2,
    AlertTriangle, Loader2, Sparkles,
} from "lucide-react";

const ICON_MAP = { ScrollText, Brain, Film };

export default function AiJobsTray() {
    const { jobs, dismissJob } = useAiJobs();
    const [collapsed, setCollapsed] = useState(false);

    const active = useMemo(
        () => jobs.filter((j) => !j.done && !j.error),
        [jobs],
    );
    const finished = useMemo(
        () => jobs.filter((j) => j.done || j.error),
        [jobs],
    );

    if (jobs.length === 0) return null;

    return (
        <div
            className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[95vw]"
            data-testid="ai-jobs-tray"
        >
            <div className="rounded-lg border border-primary/25 bg-card shadow-2xl shadow-primary/20 overflow-hidden">
                <button
                    type="button"
                    className="w-full px-3 py-2 bg-primary/[0.06] flex items-center justify-between gap-2 hover:bg-primary/[0.1] transition-colors"
                    onClick={() => setCollapsed((c) => !c)}
                    data-testid="ai-jobs-tray-toggle"
                >
                    <span className="flex items-center gap-2 text-sm font-medium">
                        <Sparkles className="h-4 w-4 text-primary" />
                        AI jobs
                        {active.length > 0 ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-primary text-primary-foreground">
                                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                {active.length} running
                            </span>
                        ) : (
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                {finished.length} complete
                            </span>
                        )}
                    </span>
                    {collapsed
                        ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </button>
                {!collapsed ? (
                    <div className="max-h-[60vh] overflow-y-auto divide-y divide-border">
                        {jobs.map((j) => (
                            <JobCard key={j.jobId} job={j} onDismiss={() => dismissJob(j.jobId)} />
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function JobCard({ job, onDismiss }) {
    const cfg = KIND_CONFIG[job.kind];
    const Icon = ICON_MAP[cfg?.icon] || Sparkles;
    const stageText = cfg?.stageLabel(job.status) || "queued";
    const percent = job.percent || 0;
    const title = job.kind === "video"
        ? job.lessonTitle || cfg.label
        : job.chapterTitle || cfg.label;

    return (
        <div className="px-3 py-2.5" data-testid={`ai-job-${job.jobId}`}>
            <div className="flex items-start gap-2">
                <div className={`mt-0.5 ${job.done ? "text-emerald-600" : job.error ? "text-rose-600" : "text-primary"}`}>
                    {job.done
                        ? <CheckCircle2 className="h-4 w-4" />
                        : job.error
                            ? <AlertTriangle className="h-4 w-4" />
                            : <Icon className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold truncate">
                            {cfg?.label} — {title}
                        </div>
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="text-muted-foreground hover:text-foreground"
                            aria-label="Dismiss"
                            data-testid={`ai-job-dismiss-${job.jobId}`}
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    <div className={`text-[11px] mt-0.5 truncate ${job.error ? "text-rose-700" : "text-muted-foreground"}`}>
                        {job.error || stageText}
                    </div>
                    <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-muted">
                        <div
                            className={`h-full transition-all ${
                                job.error
                                    ? "bg-rose-500"
                                    : job.done
                                        ? "bg-emerald-500"
                                        : "bg-primary relative overflow-hidden"
                            }`}
                            style={{ width: `${percent}%` }}
                        >
                            {!job.done && !job.error ? (
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1.6s_ease-in-out_infinite]" />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
